import { computed, getCurrentInstance, nextTick, ref } from "vue";
import { useAppStore } from "@/store/app";
import { buildContext, runRules } from "@/validation";
import { isAddressed } from "@/validation/customFieldErrors";
import { BUILDER_SCROLL_RESTORE_DELAYS } from "@/util/focusNavigation";
import { trackRuleResolved, trackRulesDetected } from "@/validation/analytics";
import type { ContextSource } from "@/validation";
import type { RuleResult, RuleTarget } from "@/validation";

/**
 * Shared validation state for one builder session.
 *
 * Module-scoped on purpose: the header, the error popover and the canvas
 * markers all read the same run. A per-component composable would give each of
 * them a different answer.
 */
const errors = ref<RuleResult[]>([]);
const recommendations = ref<RuleResult[]>([]);
const resolvedKeys = ref<Set<string>>(new Set());
const isValidating = ref(false);
/** True once Save has been pressed. Nothing is flagged before that. */
const hasRun = ref(false);
/** When each finding was first reported, for the time-to-resolve bucket. */
const detectedAt = ref<Map<string, number>>(new Map());
/**
 * Findings the registry did not produce, so `revalidate` can leave them alone.
 *
 * It decides what is fixed by re-running the rules, and anything they no longer
 * report counts as resolved. An API failure is never reported by a rule, so
 * without this it is struck through the moment anything triggers a re-check.
 */
const externalKeys = ref<Set<string>>(new Set());

export const useBuilderValidation = () => {
  const store = useAppStore();
  // Resolved rather than taken from useEmitter so the composable still works
  // outside a component instance, which is how the unit tests exercise it.
  const emitter = getCurrentInstance()?.appContext.config.globalProperties
    .emitter as { emit: (event: string, payload?: unknown) => void } | undefined;

  const snapshot = (): ReturnType<typeof buildContext> =>
    buildContext(store as unknown as ContextSource);

  const totalsFor = (ctx: ReturnType<typeof buildContext>) => ({
    errorCount: errors.value.length,
    recommendationCount: recommendations.value.length,
    recommendationsEnabled:
      ctx.builderConfig.showRecommendationsBeforeSaving !== false,
    assetId: String(store.formId || store.surveyId || store.quizId || ""),
  });

  const unresolvedErrors = computed(() =>
    errors.value.filter((result) => !resolvedKeys.value.has(result.key))
  );

  const errorCount = computed(() => unresolvedErrors.value.length);

  /**
   * Whether the builder can tell, without asking the server, that saving will
   * fail.
   *
   * Only rule findings count. An API failure describes the attempt that just
   * happened, not a state the builder can re-check — the only way to know it is
   * fixed is to save again. Letting it disable Save would make it permanent:
   * the button that clears it is the button it switches off.
   */
  const hasBlockingErrors = computed(() =>
    unresolvedErrors.value.some((result) => !externalKeys.value.has(result.key))
  );

  /**
   * Builds a fresh error list. Called on each Save press — a previous run's
   * resolved items do not carry over, so the list always describes the asset as
   * it is now.
   */
  const runErrors = (): RuleResult[] => {
    isValidating.value = true;
    try {
      const ctx = snapshot();
      errors.value = runRules(ctx, "error");
      resolvedKeys.value = new Set();
      // Rebuilt from the rules, so last run's API failures are gone until the
      // save tries again and the server repeats them.
      externalKeys.value = new Set();
      hasRun.value = true;

      const now = Date.now();
      const stamps = new Map<string, number>();
      errors.value.forEach((result) => stamps.set(result.key, now));
      detectedAt.value = stamps;

      trackRulesDetected(ctx, errors.value, totalsFor(ctx));
      return errors.value;
    } finally {
      isValidating.value = false;
    }
  };

  /**
   * Re-evaluates without rebuilding the list. Anything that no longer fires is
   * marked resolved, so the popover can strike it through and keep it visible
   * while the count drops.
   */
  /**
   * Re-runs the rules against the current builder and reconciles the standing
   * list with the result.
   *
   * Findings address elements by position, so inserting, moving or deleting an
   * element shifts every index after it. Without this the canvas marker stays
   * on the old position and ends up labelling the wrong element. Anything still
   * failing has its target refreshed; anything that no longer fires is marked
   * resolved, which is also how deleting the offending element clears it.
   */
  /**
   * Re-checks the findings already on screen.
   *
   * `includeNew` separates the two callers. A structural edit only needs the
   * existing rows re-targeted, because nothing is reported before the user asks
   * for it — adding an element must not raise its error on the spot. Asking to
   * see the list again does want rules broken since the last run, so a rule
   * violated while fixing another appears without a second request.
   */
  /** The element a finding points at, by identity rather than position. */
  const elementFor = (
    result: RuleResult,
    ctx: ReturnType<typeof buildContext>
  ) =>
    ctx.elements.find(
      (entry) => entry.elementKey === result.target?.elementKey
    )?.element;

  /**
   * Re-checks only the API findings, without running a single rule.
   *
   * Typing a label changes nothing structural, so the full re-check never
   * fires — but that keystroke is exactly what clears one of these. Kept
   * separate so it can run on every edit without re-evaluating the registry,
   * and it returns immediately when there is nothing of this kind to re-check.
   */
  const recheckExternalErrors = (): void => {
    if (!externalKeys.value.size) return;
    const ctx = snapshot();

    const next = new Set(resolvedKeys.value);
    errors.value.forEach((result) => {
      if (!externalKeys.value.has(result.key)) return;
      if (isAddressed(result, elementFor(result, ctx))) next.add(result.key);
      else next.delete(result.key);
    });
    resolvedKeys.value = next;
  };

  const revalidate = ({ includeNew = false } = {}): void => {
    if (!hasRun.value) return;
    const ctx = snapshot();
    const current = runRules(ctx, "error");
    const stillFailing = new Set(current.map((result) => result.key));

    const byKey = new Map(current.map((result) => [result.key, result]));
    const known = new Set(errors.value.map((result) => result.key));

    /**
     * Where each element sits now, by identity.
     *
     * An API failure is not re-derived by any rule, so nothing else can follow
     * its element around. Addressed by position alone it would point at
     * whichever field moved into that slot, and survive the deletion of the
     * field it was actually about.
     */
    const movedTo = new Map(
      ctx.elements.map((entry) => [
        entry.elementKey,
        { elementIndex: entry.elementIndex, slideIndex: entry.slideIndex },
      ])
    );

    const carryOver = (result: RuleResult): RuleResult | undefined => {
      const fresh = byKey.get(result.key);
      if (fresh) return { ...result, target: fresh.target };
      if (!externalKeys.value.has(result.key)) return result;

      const elementKey = result.target?.elementKey;
      // Nothing to follow it by, so leave it rather than guess it is stale.
      if (!elementKey) return result;

      const moved = movedTo.get(elementKey);
      // Its field is gone, so the failure it described cannot still stand.
      if (!moved) return undefined;
      return { ...result, target: { ...result.target, ...moved } };
    };

    errors.value = [
      ...errors.value
        .map(carryOver)
        .filter((result): result is RuleResult => Boolean(result)),
      // Appended rather than replacing the list, so resolved rows keep their
      // place and their numbering while a newly broken rule still shows up.
      ...(includeNew
        ? current.filter((result) => !known.has(result.key))
        : []),
    ];
    const nextResolved = new Set(
      errors.value
        .filter((result) =>
          externalKeys.value.has(result.key)
            ? // No rule can speak for these, so ask the weaker question: has
              // what the server objected to actually changed?
              isAddressed(result, elementFor(result, ctx))
            : !stillFailing.has(result.key)
        )
        .map((result) => result.key)
    );

    // Report only findings that cleared since the last check, so a rule is
    // never counted as resolved twice.
    const now = Date.now();
    errors.value
      .filter(
        (result) =>
          nextResolved.has(result.key) && !resolvedKeys.value.has(result.key)
      )
      .forEach((result) => {
        const since = detectedAt.value.get(result.key) ?? now;
        trackRuleResolved(ctx, result, now - since, totalsFor(ctx));
      });

    resolvedKeys.value = nextResolved;

    // Anything dropped above should stop counting as external, or its key
    // lingers and keeps a later finding from being resolvable.
    const live = new Set(errors.value.map((result) => result.key));
    externalKeys.value = new Set(
      [...externalKeys.value].filter((key) => live.has(key))
    );
  };

  /**
   * Adds findings the registry cannot produce.
   *
   * Custom field validity is the Location API's to decide, and it only answers
   * once the save has tried. Those failures arrive after `runErrors` has
   * already passed, so they are merged into the same list rather than kept in a
   * surface of their own — the user sees one place for everything blocking.
   */
  const reportExternalErrors = (results: RuleResult[]): void => {
    hasRun.value = true;
    if (!results.length) return;

    // Struck through from an earlier run, but the API has just rejected it
    // again — it is not fixed, whatever the last rule pass concluded.
    const cleared = new Set(resolvedKeys.value);
    results.forEach((result) => cleared.delete(result.key));
    resolvedKeys.value = cleared;

    const known = new Set(errors.value.map((result) => result.key));
    const added = results.filter((result) => !known.has(result.key));
    if (!added.length) return;

    const now = Date.now();
    added.forEach((result) => detectedAt.value.set(result.key, now));
    errors.value = [...errors.value, ...added];
    externalKeys.value = new Set([
      ...externalKeys.value,
      ...results.map((result) => result.key),
    ]);
  };

  /**
   * `silent` keeps the pill's count fresh without reporting.
   *
   * The count has to track the asset as it is edited, but every edit is not a
   * detection event — analytics stays on the deliberate paths, saving and
   * opening the panel, so the numbers keep meaning what they did.
   */
  const runRecommendations = ({ silent = false } = {}): RuleResult[] => {
    const ctx = snapshot();
    recommendations.value = runRules(ctx, "recommendation");
    if (!silent) {
      trackRulesDetected(ctx, recommendations.value, totalsFor(ctx));
    }
    return recommendations.value;
  };

  const isResolved = (result: RuleResult): boolean =>
    resolvedKeys.value.has(result.key);

  /** Clears state — on builder unmount, or after a successful save. */
  const reset = (): void => {
    errors.value = [];
    recommendations.value = [];
    resolvedKeys.value = new Set();
    detectedAt.value = new Map();
    externalKeys.value = new Set();
    hasRun.value = false;
  };

  /**
   * Selects the element a finding points at and opens its settings sidebar.
   *
   * Mirrors what the Picture Choice save gate already did, generalised to any
   * rule: forms address elements by index, surveys and quizzes by slide plus
   * index, and both set `isAnyElementActive` so the sidebar renders.
   */
  /**
   * Opens the surface a setting lives on.
   *
   * Shared by findings with no element to select and by the Insights rows,
   * which name the same surfaces. Not every surface is a builder tab: the quiz
   * results page and conditional logic are modals, and the Quick Add palette is
   * a sidebar, so each is reached its own way.
   */
  const focusSettingsTab = (tab?: string, quickAddTag?: string): void => {
    if (tab === "notifications") {
      store.activeBuilderTab = "notifications";
      return;
    }
    if (tab === "results") {
      store.activeBuilderTab = "edit";
      emitter?.emit("open-quiz-result-builder");
      return;
    }
    if (tab === "logic") {
      store.activeBuilderTab = "edit";
      store.showConditionalLogicModal = true;
      return;
    }
    if (tab === "styles") {
      // Footer styling lives in the right-hand styles drawer, which shares its
      // space with the element sidebar — the current selection has to be
      // dropped or the drawer stays hidden behind it.
      store.activeBuilderTab = "edit";
      store.isAnyElementActive = false;
      store.lastStatusOfRightDrawer = true;
      emitter?.emit("restore-sidebar-state", { rightSidebarOpen: true });
      return;
    }
    if (tab === "elements") {
      // Nothing exists to select yet — open the Quick Add palette and mark the
      // element so the user can see which one to drag in.
      store.activeBuilderTab = "edit";
      store.leftSidebarOpen = true;
      store.quickAddHighlightTag = quickAddTag;
      emitter?.emit("open-left-side-bar");
      return;
    }
    store.activeBuilderTab = tab && tab !== "edit" ? "settings" : "edit";
  };

  /**
   * Brings the element a finding points at into view.
   *
   * All three builders mark their selected element with `.active` inside
   * `#form-builder`, so the selection is its own anchor and no extra markup is
   * needed. (Tailwind's `active:` variants produce tokens like
   * `active:outline-none`, which this does not match.) Only that container is
   * scrolled — `scrollIntoView` moves every scrollable ancestor and drags the
   * builder header off with it.
   *
   * A finding on another slide only renders once that slide is switched in,
   * which can take a frame or two, so the lookup is retried briefly.
   */
  const SCROLL_FRAMES = 5;

  /**
   * After the canvas has finished putting its own scroll back.
   *
   * The builders snapshot the scroll on `focusin` and restore it on a schedule,
   * so that dragging a field or focusing a label does not jump the page.
   * Selecting an element focuses its label, which arms exactly that — and the
   * restore lands mid-animation, leaving the canvas where it was. This waits
   * until the last restore has run, then scrolls.
   */
  const AFTER_SCROLL_RESTORE_MS = Math.max(...BUILDER_SCROLL_RESTORE_DELAYS) + 60;

  const scrollActiveIntoView = (): void => {
    const container = document.getElementById("form-builder");
    const active = container?.querySelector(".active") as HTMLElement | null;
    if (!container || !active) return;
    if (typeof container.scrollTo !== "function") return;

    const offset =
      active.getBoundingClientRect().top - container.getBoundingClientRect().top;
    container.scrollTo({
      top:
        container.scrollTop +
        offset -
        container.clientHeight / 2 +
        active.clientHeight / 2,
      behavior: "smooth",
    });
  };

  const scrollToSelectedElement = async (attempt = 0): Promise<void> => {
    await nextTick();
    const active = document
      .getElementById("form-builder")
      ?.querySelector(".active");

    if (!active) {
      if (attempt < SCROLL_FRAMES) {
        requestAnimationFrame(() => void scrollToSelectedElement(attempt + 1));
      }
      return;
    }

    // Once now, so a selection that arms no restore moves straight away, and
    // again after the restore window for the ones that do.
    scrollActiveIntoView();
    setTimeout(scrollActiveIntoView, AFTER_SCROLL_RESTORE_MS);
  };

  /**
   * The selection shape the open sidebar can actually read.
   *
   * Surveys address elements by index, but QuizElementSidebar resolves them by
   * `{ slideId, elementId }` — handed indices it looks up `slides[null]` and
   * renders against undefined, which wedges the sidebar rather than failing
   * visibly. The id falls back the way the store's own resolver does, so
   * quizzes predating `uuid` still resolve. The canvas matches its active
   * element the same way, so the highlight needs the same pair — a quiz given
   * indices selects the element without ever marking it.
   */
  const slideSelection = (
    slideIndex: number,
    elementIndex: number
  ): {
    overlay: Record<string, unknown>;
    active: Array<string | number>;
    slide: string | number;
  } => {
    const byIndex = {
      overlay: { index: elementIndex, slideIndex, formIndex: undefined },
      active: [slideIndex, elementIndex],
      slide: slideIndex,
    };
    if (!store.quizId) return byIndex;

    const slide = (store.slides ?? [])[slideIndex];
    const element = slide?.slideData?.[elementIndex];
    const elementId =
      element?.uuid || element?.hiddenFieldQueryKey || element?.fieldKey;
    if (!slide?.id || !elementId) return byIndex;

    return {
      overlay: { slideId: slide.id, elementId },
      active: [slide.id, elementId],
      slide: slide.id,
    };
  };

  /**
   * Selects an element on the canvas and opens its sidebar.
   *
   * Shared by findings and by the Insights rows, so both reach an element the
   * same way — a lookup that only read `store.elements` would find nothing on a
   * survey or quiz, where elements live on slides.
   */
  const focusElement = (target: RuleTarget): void => {
    if (target.elementIndex === undefined) return;

    // Element settings live in the canvas sidebars, which only exist on the
    // Edit tab — selecting from any other tab would otherwise do nothing.
    store.activeBuilderTab = "edit";
    // The form-level styles drawer occupies the same place as the element
    // sidebar, so a finding that selects an element has to give up that intent
    // or the drawer keeps coming back over the panel it was sent to.
    store.lastStatusOfRightDrawer = false;
    // Findings about a field's choices belong on the Options tab. Selecting the
    // element remounts the sidebar, so the request names the element rather than
    // being consumed on read — otherwise the instance that reads it is thrown
    // away before it can render.
    store.sidebarTabTarget = target.sidebarTab
      ? { tab: target.sidebarTab, elementIndex: target.elementIndex }
      : undefined;

    // `pictureChoiceValidationTarget` has its own key contract — the settings
    // panel resolves it with getPictureChoiceElementKey. Passing the framework's
    // own element key would never match, so address by index and let the panel
    // fall back to it.
    if (target.slideIndex === undefined) {
      store.pictureChoiceValidationTarget = {
        elementIndex: target.elementIndex,
      };
      store.currentCustomSidebarPreview = target.elementIndex;
    } else {
      store.pictureChoiceValidationTarget = {
        slideIndex: target.slideIndex,
        elementIndex: target.elementIndex,
      };
      const selection = slideSelection(target.slideIndex, target.elementIndex);
      store.currentActiveIndex = selection.active;
      store.currentSlideIndex = selection.slide;
      store.sidebarOverlaySettings = selection.overlay;
    }
    store.isAnyElementActive = true;

    // The opportunity modal lives in the selected field's sidebar, so it can
    // only be opened once that field is active.
    if (target.settingsTab === "opportunities") {
      store.showOpportunitiesModal = true;
    }

    // Selecting a field below the fold leaves the user looking at the same
    // screen they clicked from, with nothing visibly changed.
    void scrollToSelectedElement();
  };

  /** Points the Insights payment row at the element that owns those settings. */
  const focusPaymentElement = (): void => {
    const entry = snapshot().elements.find(
      ({ element }) => element?.tag === "payment"
    );
    if (!entry) return;
    focusElement({
      elementIndex: entry.elementIndex,
      slideIndex: entry.slideIndex,
      elementKey: entry.elementKey,
    });
  };

  const focusResult = (result: RuleResult): void => {
    const target = result.target;
    if (!target) return;

    // Settings-level findings have no element to select; send the user to the
    // surface that owns the setting instead.
    if (target.elementIndex === undefined) {
      focusSettingsTab(target.settingsTab, target.quickAddTag);
      return;
    }

    focusElement(target);
  };

  return {
    errors,
    recommendations,
    unresolvedErrors,
    errorCount,
    hasBlockingErrors,
    isValidating,
    hasRun,
    runErrors,
    reportExternalErrors,
    recheckExternalErrors,
    revalidate,
    runRecommendations,
    isResolved,
    reset,
    focusResult,
    focusSettingsTab,
    focusPaymentElement,
  };
};
